import { useEffect, useMemo, useRef, useState } from "react";
import {
  Paperclip,
  Camera,
  Mic,
  Square,
  Upload,
  X,
  FileText,
  Music,
  Loader2,
  Download,
  Image as ImageIcon,
  AlertCircle,
} from "lucide-react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import api, { formatApiError, API } from "../lib/api";
import { toast } from "sonner";

const formatBytes = (n) => {
  if (n == null) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

/**
 * TaskAttachments
 *
 * Two modes:
 * 1. `taskId` provided → talks to /api/attachments directly. Live uploads.
 * 2. `taskId` is null   → stores File objects in a local queue (`pendingFiles`)
 *                         that the parent must flush after creating the task.
 *                         The parent reads queue via `pendingFilesRef`.
 */
export default function TaskAttachments({
  taskId,
  pendingFilesRef,
  accentColor = "#0281FF",
}) {
  const [attachments, setAttachments] = useState([]); // server-side
  const [pending, setPending] = useState([]); // local for new-task mode
  const [quota, setQuota] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState("");
  const [thumbTokens, setThumbTokens] = useState({}); // id -> objectURL for thumbs
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);

  // Keep parent's pending-files ref in sync (so it can flush after task create)
  useEffect(() => {
    if (pendingFilesRef) {
      pendingFilesRef.current = pending;
    }
  }, [pending, pendingFilesRef]);

  // Initial load + quota fetch
  useEffect(() => {
    api
      .get("/attachments/quota")
      .then(({ data }) => setQuota(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!taskId) {
      setAttachments([]);
      return;
    }
    api
      .get(`/attachments?task_id=${taskId}`)
      .then(({ data }) => setAttachments(data?.attachments || []))
      .catch(() => {});
  }, [taskId]);

  // Lazy-load image thumbnails into blob URLs (so the Authorization header is honored)
  useEffect(() => {
    let cancelled = false;
    const fetchThumb = async (att) => {
      if (thumbTokens[att.id] || att.kind !== "image") return;
      try {
        const res = await fetch(`${API}/attachments/${att.id}/thumb`, {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("flowtasks_access_token") || ""}`,
          },
        });
        if (!res.ok) return;
        const blob = await res.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        setThumbTokens((m) => ({ ...m, [att.id]: url }));
      } catch {
        // skip
      }
    };
    for (const att of attachments) {
      fetchThumb(att);
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments]);

  // Revoke object URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(thumbTokens).forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Upload helpers ----------
  const allowedMimes = useMemo(
    () => quota?.allowed_mimes || [
      "image/jpeg",
      "image/png",
      "image/webp",
      "image/heic",
      "image/heif",
      "application/pdf",
      "audio/mpeg",
      "audio/mp4",
      "audio/wav",
      "audio/webm",
      "audio/ogg",
    ],
    [quota]
  );

  const validateFile = (file) => {
    setError("");
    const mime = file.type?.toLowerCase() || "";
    if (!allowedMimes.includes(mime)) {
      setError(`Type "${mime || "unknown"}" isn't supported.`);
      return false;
    }
    const cap = quota?.file_max_bytes || 10 * 1024 * 1024;
    if (file.size > cap) {
      setError(
        `File is ${formatBytes(file.size)} — exceeds the ${formatBytes(cap)} per-file limit.`
      );
      return false;
    }
    if (quota && quota.used_bytes + file.size > quota.total_bytes) {
      setError(
        `Storage full — ${formatBytes(quota.used_bytes)} of ${formatBytes(quota.total_bytes)} used.`
      );
      return false;
    }
    return true;
  };

  const uploadOne = async (file) => {
    if (!validateFile(file)) return null;
    if (!taskId) {
      // Queue locally until task is saved
      const queued = {
        _local_id: `local-${Date.now()}-${Math.random()}`,
        file,
        filename: file.name,
        size_bytes: file.size,
        mime_type: file.type,
        kind: file.type.startsWith("image/")
          ? "image"
          : file.type === "application/pdf"
          ? "pdf"
          : "audio",
        preview_url: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
      };
      setPending((p) => [...p, queued]);
      return queued;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const { data } = await api.post(`/attachments/upload?task_id=${taskId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments((a) => [...a, data]);
      // refresh quota
      api
        .get("/attachments/quota")
        .then(({ data }) => setQuota(data))
        .catch(() => {});
      toast.success(`Attached "${data.filename}"`);
      return data;
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiError(e.response?.data?.detail);
      toast.error(detail || `Upload failed (${status || "?"})`);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = async (fileList) => {
    if (!fileList?.length) return;
    for (const f of Array.from(fileList)) {
      await uploadOne(f);
    }
  };

  const onPickFiles = (e) => {
    handleFiles(e.target.files);
    e.target.value = "";
  };

  // ---------- Drag-drop ----------
  const [dragOver, setDragOver] = useState(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files) handleFiles(e.dataTransfer.files);
  };

  // ---------- Audio recording ----------
  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Mic recording isn't supported in this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeCandidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg;codecs=opus",
      ];
      const mime = mimeCandidates.find((m) => MediaRecorder.isTypeSupported?.(m)) || "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recordedChunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(recordedChunksRef.current, {
          type: mr.mimeType || "audio/webm",
        });
        // Try to use a clean mime that backend allows
        const cleanMime = (mr.mimeType || "audio/webm").split(";")[0];
        const file = new File([blob], `voice-memo-${Date.now()}.${cleanMime.split("/")[1] || "webm"}`, {
          type: cleanMime,
        });
        await uploadOne(file);
        setRecording(false);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error(e.message || "Couldn't access microphone");
      setRecording(false);
    }
  };

  const stopRecording = () => {
    try {
      mediaRecorderRef.current?.stop();
    } catch {}
  };

  // ---------- Delete ----------
  const onDelete = async (att) => {
    if (att._local_id) {
      setPending((p) => p.filter((x) => x._local_id !== att._local_id));
      if (att.preview_url) URL.revokeObjectURL(att.preview_url);
      return;
    }
    if (!window.confirm(`Delete "${att.filename}"?`)) return;
    try {
      await api.delete(`/attachments/${att.id}`);
      setAttachments((a) => a.filter((x) => x.id !== att.id));
      api
        .get("/attachments/quota")
        .then(({ data }) => setQuota(data))
        .catch(() => {});
      toast.success("Attachment removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const onDownload = async (att) => {
    try {
      const res = await fetch(`${API}/attachments/${att.id}/download`, {
        credentials: "include",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("flowtasks_access_token") || ""}`,
        },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e.message || "Download failed");
    }
  };

  const allItems = [
    ...attachments.map((a) => ({ ...a, _server: true })),
    ...pending.map((a) => ({ ...a, _server: false })),
  ];

  const usedPct =
    quota && quota.total_bytes
      ? Math.min(100, Math.round((quota.used_bytes / quota.total_bytes) * 100))
      : 0;

  return (
    <div data-testid="task-attachments-section">
      <div className="flex items-center justify-between mb-1.5">
        <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <Paperclip size={11} strokeWidth={2} /> Attachments
        </Label>
        {quota && (
          <span className="text-[10px] text-muted-foreground font-mono">
            {formatBytes(quota.used_bytes)} / {formatBytes(quota.total_bytes)}
          </span>
        )}
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        data-testid="attachments-dropzone"
        className="rounded-lg border-2 border-dashed p-3 transition-colors"
        style={{
          borderColor: dragOver ? accentColor : "hsl(var(--border))",
          backgroundColor: dragOver ? `${accentColor}10` : "transparent",
        }}
      >
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileInputRef}
            data-testid="attachments-file-input"
            type="file"
            multiple
            accept={allowedMimes.join(",")}
            onChange={onPickFiles}
            className="hidden"
          />
          <input
            ref={cameraInputRef}
            data-testid="attachments-camera-input"
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onPickFiles}
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            data-testid="attachments-pick-btn"
            disabled={uploading}
          >
            <Upload size={13} className="mr-1.5" /> Choose file
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => cameraInputRef.current?.click()}
            data-testid="attachments-camera-btn"
            disabled={uploading}
            className="sm:inline-flex"
          >
            <Camera size={13} className="mr-1.5" /> Take photo
          </Button>
          {!recording ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={startRecording}
              data-testid="attachments-mic-btn"
              disabled={uploading}
            >
              <Mic size={13} className="mr-1.5" /> Voice memo
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              onClick={stopRecording}
              data-testid="attachments-stop-recording-btn"
              className="bg-red-500 hover:bg-red-600 text-white animate-pulse"
            >
              <Square size={11} className="mr-1.5" /> Stop recording
            </Button>
          )}
          {uploading && (
            <span
              className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground"
              data-testid="attachments-uploading"
            >
              <Loader2 size={11} className="animate-spin" /> Uploading…
            </span>
          )}
          <span className="text-[10px] text-muted-foreground ml-auto hidden sm:block">
            or drop here · images · PDF · audio
          </span>
        </div>

        {error && (
          <div
            data-testid="attachments-error"
            className="mt-2 flex items-start gap-1.5 text-[11px] text-red-500"
          >
            <AlertCircle size={11} className="mt-0.5 shrink-0" /> {error}
          </div>
        )}
      </div>

      {/* Quota bar */}
      {quota && quota.total_bytes && (
        <div className="mt-2 h-1 rounded-full bg-secondary overflow-hidden">
          <div
            className="h-full transition-all duration-300"
            style={{
              width: `${usedPct}%`,
              backgroundColor: usedPct >= 90 ? "#EF4444" : accentColor,
            }}
          />
        </div>
      )}

      {/* Grid of attachments */}
      {allItems.length > 0 && (
        <ul
          data-testid="attachments-list"
          className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3"
        >
          {allItems.map((att) => (
            <li
              key={att.id || att._local_id}
              data-testid={`attachment-item-${att.id || att._local_id}`}
              className="group relative rounded-md border border-border bg-card overflow-hidden"
            >
              {/* Preview */}
              <div className="aspect-[4/3] bg-secondary/30 flex items-center justify-center overflow-hidden">
                {att.kind === "image" ? (
                  <img
                    src={att.preview_url || thumbTokens[att.id]}
                    alt={att.filename}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : att.kind === "pdf" ? (
                  <FileText
                    size={28}
                    strokeWidth={1.5}
                    style={{ color: "#EF4444" }}
                    aria-label="PDF"
                  />
                ) : att.kind === "audio" ? (
                  <Music
                    size={28}
                    strokeWidth={1.5}
                    style={{ color: accentColor }}
                    aria-label="Audio"
                  />
                ) : (
                  <ImageIcon size={28} strokeWidth={1.5} className="text-muted-foreground" />
                )}
                {!att._server && (
                  <span className="absolute top-1 left-1 text-[9px] uppercase tracking-wider bg-amber-500 text-black font-bold px-1.5 py-0.5 rounded">
                    Pending
                  </span>
                )}
              </div>

              {/* Footer */}
              <div className="px-2 py-1.5 flex items-center gap-1">
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-medium truncate" title={att.filename}>
                    {att.filename}
                  </div>
                  <div className="text-[10px] text-muted-foreground font-mono">
                    {formatBytes(att.size_bytes)}
                  </div>
                </div>
                {att._server && att.kind === "audio" && (
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-secondary"
                    title="Play"
                    onClick={async () => {
                      const res = await fetch(`${API}/attachments/${att.id}/download`, {
                        credentials: "include",
                        headers: {
                          Authorization: `Bearer ${
                            localStorage.getItem("flowtasks_access_token") || ""
                          }`,
                        },
                      });
                      if (!res.ok) return;
                      const blob = await res.blob();
                      const audio = new Audio(URL.createObjectURL(blob));
                      audio.play();
                    }}
                    data-testid={`attachment-play-${att.id}`}
                  >
                    <Music size={11} />
                  </button>
                )}
                {att._server && (
                  <button
                    type="button"
                    onClick={() => onDownload(att)}
                    className="p-1 rounded hover:bg-secondary"
                    title="Download"
                    data-testid={`attachment-download-${att.id}`}
                  >
                    <Download size={11} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onDelete(att)}
                  className="p-1 rounded text-muted-foreground hover:text-red-500 hover:bg-red-500/10"
                  title="Remove"
                  data-testid={`attachment-delete-${att.id || att._local_id}`}
                >
                  <X size={12} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Flush any pending (queued) files in `pendingFilesRef.current` to the server.
 * Called by TaskModal right after a new task is created.
 */
export async function flushPendingAttachments(pendingFiles, taskId) {
  if (!pendingFiles?.length || !taskId) return [];
  const uploaded = [];
  for (const p of pendingFiles) {
    try {
      const fd = new FormData();
      fd.append("file", p.file, p.filename);
      const { data } = await api.post(`/attachments/upload?task_id=${taskId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      uploaded.push(data);
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail);
      toast.error(`"${p.filename}" — ${detail || "upload failed"}`);
    } finally {
      if (p.preview_url) URL.revokeObjectURL(p.preview_url);
    }
  }
  return uploaded;
}
